import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { Provider } from "../order";
import {
  CarrierSchema,
  ServiceSchema,
} from "./fulfillment-carriers-and-services";
import { RedoShopifyOrderCancelReason } from "./order-cancel-reason";
import {
  PlacedOnHoldSource,
  ReleasedFromHoldSource,
} from "./placed-on-hold-source";
import { PrintableDocument } from "./print-job";
import {
  SelectedShippingRateSetBy,
  SelectedShippingRateSetByInfoSchema,
} from "./selected-shipping-rate-set-by";
import { ShopifyFulfillmentHoldReason } from "./shopify-fulfillment-schemas";

export enum OMSEventType {
  PACKING_SLIP_PRINTED = "packing_slip_printed",
  PACKING_SLIP_UNPRINTED = "packing_slip_unprinted",
  LABEL_PRINTED = "label_printed",
  LABEL_AND_PACKING_SLIP_PRINTED = "label_and_packing_slip_printed",
  PICK_LIST_PRINTED = "pick_list_printed",
  PICK_LIST_UNPRINTED = "pick_list_unprinted",
  PERSONALIZED_SLIP_PRINTED = "personalized_slip_printed",
  PERSONALIZED_SLIP_UNPRINTED = "personalized_slip_unprinted",
  MERGED = "merged",
  AUTO_MERGED = "auto_merged",
  UNMERGED = "unmerged",
  CREATED_FROM_SPLIT = "created_from_split",
  SPLIT = "split",
  AUTO_SPLIT = "auto_split",
  LABEL_PURCHASED = "label_purchased",
  LABEL_VOIDED = "label_voided",
  VERIFIED = "verified",
  UNVERIFIED = "unverified",
  READY_FOR_PICKUP_EMAIL_SENT = "ready_for_pickup_email_sent",
  ASSIGNED_TO_FULFILLMENT_SERVICE = "assigned_to_fulfillment_service",
  RESHIPPED = "reshipped",
  CREATED_FROM_RESHIP = "created_from_reship",
  MARKED_AS_FULFILLED = "marked_as_fulfilled",
  MERCHANT_AUTO_SPLIT = "merchant_auto_split",
  CREATED_FROM_MERCHANT_AUTO_SPLIT = "created_from_merchant_auto_split",
  PACKING_SLIP_SCANNED = "packing_slip_scanned",
  /** @deprecated Use PlacedOnHoldEventSchema instead */
  PLACED_ON_HOLD_DUE_TO_ADDRESS_VERIFICATION_FAILURE = "placed_on_hold_due_to_address_verification_failure",
  SENT_EMAIL_TO_CUSTOMER_TO_VERIFY_ADDRESS = "sent_email_to_customer_to_verify_address",
  MARKED_AS_READY_FOR_PICKUP = "marked_as_ready_for_pickup",
  MARKED_AS_PICKED_UP = "marked_as_picked_up",
  OVERRODE_AS_RECEIVED = "overrode_as_received",
  AUTO_PRINTED = "auto_printed",
  ADDED_TO_PICK_SESSION = "added_to_pick_session",
  PICK_SESSION_CANCELLED = "pick_session_cancelled",
  MARKED_AS_PICKED = "marked_as_picked",
  MARKED_AS_PROBLEM = "marked_as_problem",
  ASSIGNED_TO_TOTE = "assigned_to_tote",
  SHIPPING_SERVICE_SELECTED = "shipping_service_selected",
  PLACED_ON_HOLD = "placed_on_hold",
  RELEASED_FROM_HOLD = "released_from_hold",
  TOTES_RELEASED = "totes_released",
  RELEASED_FOR_REPICK = "released_for_repick",
  ITEM_SCANNED = "item_scanned",
  ITEM_UNSCANNED = "item_unscanned",
  STALE_PICK_RELEASED = "stale_pick_released",
  MANUAL_SHIPMENT_CREATED = "manual_shipment_created",
  SHIPMENT_TAGS_UPDATED = "shipment_tags_updated",
  ORDER_CANCELLED = "order_cancelled",
  COMMENTSOLD_RECEIVED = "commentsold_received",
  COMMENTSOLD_NOT_RECEIVED = "commentsold_not_received",
}

const BaseEventSchema = z.object({
  userId: zExt.objectId().nullish(),
  createdAt: z.date(),
});

const PackingSlipPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PACKING_SLIP_PRINTED),
  autoPrinted: z.boolean().nullish(),
});

const PackingSlipUnprintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PACKING_SLIP_UNPRINTED),
});

const LabelPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.LABEL_PRINTED),
});

const LabelAndPackingSlipPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.LABEL_AND_PACKING_SLIP_PRINTED),
});

const PickListPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PICK_LIST_PRINTED),
});

const PickListUnprintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PICK_LIST_UNPRINTED),
});

const PersonalizedSlipPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PERSONALIZED_SLIP_PRINTED),
});

const PersonalizedSlipUnprintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PERSONALIZED_SLIP_UNPRINTED),
});

const BaseMergeEventSchema = BaseEventSchema.extend({
  mergeOrders: z.array(
    z.object({ orderId: z.string(), orderName: z.string() }),
  ),
});

const MergedEventSchema = BaseMergeEventSchema.extend({
  type: z.literal(OMSEventType.MERGED),
});

const UnmergedEventSchema = BaseMergeEventSchema.extend({
  type: z.literal(OMSEventType.UNMERGED),
});

const AutoMergedEventSchema = BaseMergeEventSchema.extend({
  type: z.literal(OMSEventType.AUTO_MERGED),
});

const CreatedFromSplitEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.CREATED_FROM_SPLIT),
  splitFromGroup: zExt.objectId(),
});

const SplitEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.SPLIT),
});

const AutoSplitEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.AUTO_SPLIT),
});

const LabelPurchasedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.LABEL_PURCHASED),
});

const LabelVoidedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.LABEL_VOIDED),
});

const VerifiedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.VERIFIED),
});

const UnverifiedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.UNVERIFIED),
});

const ReadyForPickupEmailSentEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.READY_FOR_PICKUP_EMAIL_SENT),
});

const AssignedToFulfillmentServiceEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ASSIGNED_TO_FULFILLMENT_SERVICE),
  assignedTo: z.object({
    teamId: zExt.objectId(),
    teamName: z.string(),
    fromAutomations: z.boolean(),
  }),
});

const ReshippedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.RESHIPPED),
  createdDuplicate: zExt.objectId(),
});

const CreatedFromReshipEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.CREATED_FROM_RESHIP),
  duplicateOf: zExt.objectId(),
});

const MerchantAutoSplitEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MERCHANT_AUTO_SPLIT),
});

const CreatedFromMerchantAutoSplitEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.CREATED_FROM_MERCHANT_AUTO_SPLIT),
  splitFromGroup: zExt.objectId(),
});

const MarkedAsFulfilledEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MARKED_AS_FULFILLED),
  notifyMarketplace: z.boolean().nullish(),
});

const PackingSlipScannedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PACKING_SLIP_SCANNED),
});

/** @deprecated Use PlacedOnHoldDueToShippingServiceSelectionFailureEventSchema instead */
const PlacedOnHoldDueToAddressVerificationFailureEventSchema =
  BaseEventSchema.extend({
    type: z.literal(
      OMSEventType.PLACED_ON_HOLD_DUE_TO_ADDRESS_VERIFICATION_FAILURE,
    ),
  });

const SentEmailToCustomerToVerifyAddressEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.SENT_EMAIL_TO_CUSTOMER_TO_VERIFY_ADDRESS),
  templateId: z.string().nullish(),
});

const MarkedAsReadyForPickupEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MARKED_AS_READY_FOR_PICKUP),
});

const MarkedAsPickedUpEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MARKED_AS_PICKED_UP),
});

const OverrodeAsReceivedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.OVERRODE_AS_RECEIVED),
});

const AutoPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.AUTO_PRINTED),
  documentType: createEnum(PrintableDocument, {
    enumName: "PrintableDocument",
  }),
});

const AddedToPickSessionEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ADDED_TO_PICK_SESSION),
  pickSessionId: z.string(),
  assignedPicker: zExt.objectId().nullish(),
});

const PickSessionCancelledEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PICK_SESSION_CANCELLED),
  pickSessionId: z.string(),
});

const MarkedAsPickedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MARKED_AS_PICKED),
  pickSessionId: z.string().nullish(),
});

const MarkedAsProblemEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MARKED_AS_PROBLEM),
  pickSessionId: z.string().nullish(),
  missingItemsCount: z.number().nullish(),
});

const AssignedToToteEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ASSIGNED_TO_TOTE),
  toteId: z.string(),
  toteName: z.string(),
  pickSessionId: z.string().nullish(),
});

const ShippingServiceSelectedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.SHIPPING_SERVICE_SELECTED),
  shippingService: z.object({ carrier: CarrierSchema, service: ServiceSchema }),
  selectedShippingRateSetBy: createEnum(SelectedShippingRateSetBy, {
    enumName: "SelectedShippingRateSetBy",
  }).nullish(),
  selectedShippingRateSetByInfo: SelectedShippingRateSetByInfoSchema.nullish(),
});

export const PlacedOnHoldEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PLACED_ON_HOLD),
  source: createEnum(PlacedOnHoldSource, { enumName: "PlacedOnHoldSource" }),
  automationId: zExt.objectId().nullish(),
  automationName: z.string().nullish(),
  marketplaceSyncProvider: createEnum(Provider, {
    enumName: "Provider",
  }).nullish(),
  reason: createEnum(ShopifyFulfillmentHoldReason, {
    enumName: "ShopifyFulfillmentHoldReason",
  }).nullish(),
  reasonNotes: z.string().nullish(),
});

export const ReleasedFromHoldEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.RELEASED_FROM_HOLD),
  source: createEnum(ReleasedFromHoldSource, {
    enumName: "ReleasedFromHoldSource",
  }),
});

const TotesReleasedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.TOTES_RELEASED),
  toteIds: z.array(z.string()),
  toteNames: z.array(z.string()),
});

const StalePickReleasedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.STALE_PICK_RELEASED),
  days: z.number(),
});

const ReleasedForRepickEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.RELEASED_FOR_REPICK),
});

export const ItemScannedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ITEM_SCANNED),
  lineItemExternalId: z.string(),
  sku: z.string().nullish(),
  title: z.string().nullish(),
  quantity: z.number().nullish(),
});

export const ItemUnscannedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ITEM_UNSCANNED),
  lineItemExternalId: z.string(),
  sku: z.string().nullish(),
  title: z.string().nullish(),
  quantity: z.number().nullish(),
});

const ManualShipmentCreatedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MANUAL_SHIPMENT_CREATED),
});

const ShipmentTagsUpdatedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.SHIPMENT_TAGS_UPDATED),
  addedTagNames: z.array(z.string()),
  removedTagNames: z.array(z.string()),
});

const OrderCancelledEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ORDER_CANCELLED),
  reason: createEnum(RedoShopifyOrderCancelReason, {
    enumName: "RedoShopifyOrderCancelReason",
  }).nullish(),
});

const CommentsoldReceivedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.COMMENTSOLD_RECEIVED),
});

const CommentsoldNotReceivedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.COMMENTSOLD_NOT_RECEIVED),
});

const OMSEventSchema_ = z.discriminatedUnion("type", [
  PackingSlipPrintedEventSchema,
  PackingSlipUnprintedEventSchema,
  LabelPrintedEventSchema,
  LabelAndPackingSlipPrintedEventSchema,
  PickListPrintedEventSchema,
  PickListUnprintedEventSchema,
  PersonalizedSlipPrintedEventSchema,
  PersonalizedSlipUnprintedEventSchema,
  MergedEventSchema,
  UnmergedEventSchema,
  AutoMergedEventSchema,
  CreatedFromSplitEventSchema,
  SplitEventSchema,
  AutoSplitEventSchema,
  LabelPurchasedEventSchema,
  LabelVoidedEventSchema,
  VerifiedEventSchema,
  UnverifiedEventSchema,
  ReadyForPickupEmailSentEventSchema,
  AssignedToFulfillmentServiceEventSchema,
  ReshippedEventSchema,
  CreatedFromReshipEventSchema,
  MerchantAutoSplitEventSchema,
  CreatedFromMerchantAutoSplitEventSchema,
  MarkedAsFulfilledEventSchema,
  PackingSlipScannedEventSchema,
  PlacedOnHoldDueToAddressVerificationFailureEventSchema,
  SentEmailToCustomerToVerifyAddressEventSchema,
  MarkedAsReadyForPickupEventSchema,
  MarkedAsPickedUpEventSchema,
  OverrodeAsReceivedEventSchema,
  AutoPrintedEventSchema,
  AddedToPickSessionEventSchema,
  PickSessionCancelledEventSchema,
  MarkedAsPickedEventSchema,
  MarkedAsProblemEventSchema,
  AssignedToToteEventSchema,
  ShippingServiceSelectedEventSchema,
  PlacedOnHoldEventSchema,
  ReleasedFromHoldEventSchema,
  TotesReleasedEventSchema,
  ReleasedForRepickEventSchema,
  ItemScannedEventSchema,
  ItemUnscannedEventSchema,
  StalePickReleasedEventSchema,
  ManualShipmentCreatedEventSchema,
  ShipmentTagsUpdatedEventSchema,
  OrderCancelledEventSchema,
  CommentsoldReceivedEventSchema,
  CommentsoldNotReceivedEventSchema,
]);
export const OMSEventSchema: z.ZodType<OMSEvent> = OMSEventSchema_;
export type OMSEvent = {} & z.infer<typeof OMSEventSchema_>;

export const hiddenEvents = [
  OMSEventType.CREATED_FROM_SPLIT,
  OMSEventType.CREATED_FROM_RESHIP,
  OMSEventType.AUTO_SPLIT,
];
